import React, { useState, useContext, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import Utils from "Utils/utils";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import { exportProcessingState } from "Components/popup/ExportProcessing";
import { exportEmailNotifyState } from "Components/popup/ExportEmailNotify";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import SequenceUtils from "Utils/SequenceUtils";
import ExportBtn from "Components/common/ExportBtn";
import PageLimit from "Components/common/PageLimit";
import SearchInputByKeyword from "Components/common/SearchInputByKeyword";
import ContactSelectedCount from "Components/common/ContactSelectedCount";
import Button from "Components/common/Button";
import { ReactComponent as FilterIcon } from "Assets/svg/filter.svg";
import userDetail from "Stores/userDetail";
import { outboxStoreData } from "./outboxStore";
import inboxV2Store from "./InboxV2/inboxV2Store";
import { OutboxContext } from "./main";
import { ReactComponent as Downarrow } from "Assets/svg/donw-arrow.svg";
import { ReactComponent as AllRepliesIcon } from "Assets/svg/outbox/filters/all-replies.svg";
import { ReactComponent as ReadIcon } from "Assets/svg/outbox/filters/read.svg";
import { ReactComponent as UnreadIcon } from "Assets/svg/outbox/filters/unread.svg";
import { ReactComponent as AllSentimentsIcon } from "Assets/svg/outbox/filters/all-sentiments.svg";
import { ReactComponent as PositiveIcon } from "Assets/svg/outbox/filters/positive.svg";
import { ReactComponent as NegativeIcon } from "Assets/svg/outbox/filters/negative.svg";
import { ReactComponent as NeutralIcon } from "Assets/svg/outbox/filters/neutral.svg";
import { ReactComponent as NotAnalyzedIcon } from "Assets/svg/outbox/filters/not-analyzed.svg";
import { ReactComponent as AllBouncesIcon } from "Assets/svg/outbox/filters/all-bounces.svg";
import { ReactComponent as HardBounceIcon } from "Assets/svg/outbox/filters/hard-bounce.svg";
import { ReactComponent as SoftBounceIcon } from "Assets/svg/outbox/filters/soft-bounce.svg";
import { ReactComponent as RepliesTriggerIcon } from "Assets/svg/outbox/filters/replies-trigger.svg";
import { ReactComponent as SentimentTriggerIcon } from "Assets/svg/outbox/filters/sentiment-trigger.svg";
import { ReactComponent as BounceTriggerIcon } from "Assets/svg/outbox/filters/replies-trigger.svg";

export const FilterComponent = observer((props = {}) => {
  const { showUpdateBtns = false, hideBulkActions = false } = props || {}
  const {
    inputValues = {},
    handleStateChange = () => { },
    setInputValues = () => { },
    allEmailsSelectedInView = false,
    setAllEmailsSelectedInView = () => { },
    setAllEmailsSelectedCheckbox = () => { },
    getReloadOutboxCardData = () => { },
    setData = () => { }
  } = useContext(OutboxContext);
  const [selectedCount, setSelectedCount] = useState(0);
  const [markReadUnreadDisabled, setMarkReadUnreadDisabled] = useState(false);
  const { name = "" } = outboxStoreData?.activeFilter;
  const { limit = 0, outboxData = {}, dataLoading = false } = inputValues || {};
  const { totalElements = 0 } = outboxData || {};
  const { result = [] } = outboxData || {};

  const handleLimit = (value = 10) => {
    outboxStoreData.setCurrentPage(1);
    handleStateChange("limit", value);
  };

  const handleSearchByRecipient = (value = "") => {
    const filterObject = outboxStoreData?.filterObject || {};
    const useSearchTerm =
      userDetail?.userFeatureFlag?.inboxV2Enabled &&
      (outboxStoreData?.activeFilter?.id === "REPLY_COUNT" ||
        name?.toLowerCase() === "replied");
    if (useSearchTerm) {
      const nextTerm = value || "";
      if ((filterObject?.searchTerm || "") === nextTerm) return;
      outboxStoreData.setFilterObject({
        ...filterObject,
        searchTerm: nextTerm,
        toAddress: []
      });
    } else {
      const next = value ? [value] : [];
      if ((filterObject?.toAddress || []).join() === next.join()) return;
      outboxStoreData.setFilterObject({
        ...filterObject,
        toAddress: next,
        searchTerm: ""
      });
      setData(prev => ({ ...prev, toAddress: next }));
    }
    outboxStoreData.setCurrentPage(1);
    getReloadOutboxCardData(false, true);
  };

  const limitObj = {
    limitPerPage: limit,
    text: "Showing",
    cbk: handleLimit,
    totalCount: totalElements
  };

  const getSelectedCount = () => {
    let count = 0;
    if (result?.length > 0) {
      count = ([...result] || []).reduce(
        (acc, item) => acc + (item?.isChecked ? 1 : 0),
        0
      );
    }
    setSelectedCount(count);
  };

  const exportCbk = () => {
    if (!allEmailsSelectedInView) {
      let tempEmailIds = [];
      if (result?.length > 0) {
        tempEmailIds =
          [...result]
            ?.filter(item => item?.isChecked)
            .map(item => item?.task?.id) || [];
      }
      handleExportAction(tempEmailIds);
    } else {
      handleExportAction([]);
    }
  };

  const constructExportPayload = (emailIds = []) => {
    if (!allEmailsSelectedInView) {
      return { outboxPayload: { ids: emailIds } };
    } else {
      let tempFilters = outboxStoreData.getFiltersPayload();
      return {
        outboxPayload: {
          ids: [],
          filters: { ...tempFilters, all: true }
        }
      };
    }
  };

  const clearSelectedLogs = (isClearLogs = false) => {
    setAllEmailsSelectedCheckbox(!isClearLogs);
    setAllEmailsSelectedInView(!isClearLogs);
    if (result?.length > 0) {
      setInputValues({
        ...inputValues,
        outboxData: {
          ...inputValues?.outboxData,
          result: inputValues?.outboxData?.result.map(item => ({
            ...item,
            isChecked: !isClearLogs
          }))
        }
      });
    }
  };

  const handleExportResponse = downloadLink => {
    if (!downloadLink) {
      exportProcessingState.setShowPopup(false);
      toasterState.setToastMsg(
        "Something went wrong. Please try again",
        "failure"
      );
    } else {
      exportProcessingState.completeAnimation();
      setTimeout(() => {
        Utils.downloadExportedContact(downloadLink);
        exportProcessingState.setShowPopup(false);
        setAllEmailsSelectedInView(false);
        clearSelectedLogs(true);
        toasterState.setToastMsg("Export is successfully completed", "success");
      }, 1000);
    }
  };

  const handleExportAction = async (emailIds = []) => {
    exportProcessingState.setShowPopup(true);
    const config = {
      url: URLS.exportOutboxEmails,
      method: "POST",
      data: constructExportPayload(emailIds)
    };
    let response = await makeApi(config);
    const { status = "", type = "", csvData = [] } = response?.data || {};
    const typeToCompare = "limit_reached";
    if (+status === 200 && type?.toLowerCase() === typeToCompare) {
      exportProcessingState.completeAnimation();
      exportProcessingState.setShowPopup(false);
      exportEmailNotifyState.setShowText("emails");
      exportEmailNotifyState.setShowPopup(true);
      setAllEmailsSelectedInView(false);
      clearSelectedLogs(true);
    } else if (+status === 200 && csvData?.length > 0) {
      Utils.exportCSVFile(
        [],
        csvData,
        "Salesgear-Inbox",
        handleExportResponse,
        true
      );
    } else {
      setAllEmailsSelectedInView(false);
      clearSelectedLogs(true);
      exportProcessingState.setShowPopup(false);
      toasterState.setToastMsg(
        "Something went wrong. Please try again",
        "failure"
      );
    }
  };

  const getSelectedTaskIds = () => {
    if (result?.length > 0) {
      return [...result]
        ?.filter(item => item?.isChecked)
        .map(item => item?.task?.id)
        .filter(id => id) || [];
    }
    return [];
  };

  const handleMarkAsRead = async () => {
    try {
      setMarkReadUnreadDisabled(true);
      if (!allEmailsSelectedInView) {
        const taskIds = getSelectedTaskIds();
        if (taskIds.length === 0) {
          toasterState.setToastMsg("Please select at least one email", "failure");
          return;
        }
        const totalOutboxTasks = taskIds.length;
        const response = await outboxStoreData.bulkMarkAsRead(
          taskIds,
          getReloadOutboxCardData,
          false,
          totalOutboxTasks
        );
        handleMarkAsReadResponse(response, taskIds.length);
      } else {
        const totalOutboxTasks = totalElements;
        const response = await outboxStoreData.bulkMarkAsRead(
          [],
          getReloadOutboxCardData,
          true,
          totalOutboxTasks
        );
        handleMarkAsReadResponse(response);
      }
    } finally {
      setMarkReadUnreadDisabled(false);
    }
  };

  const handleMarkAsReadResponse = (response, selectedCount = 0) => {
    if (!response) {
      const isSingle = selectedCount === 1 || selectedCount === 0;
      toasterState.setToastMsg(
        isSingle
          ? "Failed to mark message as read."
          : "Failed to mark messages as read. Please try again",
        "failure"
      );
      return;
    }

    const {
      success = false,
      bulkOperationResponse = {}
    } = response || {};

    if (!success) {
      const isSingle = selectedCount === 1 || selectedCount === 0;
      toasterState.setToastMsg(
        isSingle
          ? "Failed to mark message as read."
          : "Failed to mark messages as read. Please try again",
        "failure"
      );
      return;
    }

    const {
      activeJob = false,
      background = false,
      bulkOperationResult = {}
    } = bulkOperationResponse || {};

    if (activeJob) {
      clearSelectedLogs(true);
      SequenceUtils.showActiveJobPopupForBulkActions();
      return;
    }

    if (background) {
      confirmationPopupState.setPopupValues({
        content: "Marking messages as read. This may take a few minutes.",
        actionBtnText: "Ok",
        needCancelBtn: false,
        callback: () => confirmationPopupState.setShowPopup(false)
      });
      confirmationPopupState.setShowPopup(true);
      clearSelectedLogs(true);
      return;
    }

    const {
      total = 0,
      success: successCount = 0,
      failed = 0
    } = bulkOperationResult || {};

    const isSingle = total === 1 || selectedCount === 1;

    if (total > 0) {
      if (successCount > 0 && failed === 0) {
        if (isSingle) {
          toasterState.setToastMsg("Message marked as read.", "success");
        } else {
          toasterState.setToastMsg(
            `${successCount} message${successCount > 1 ? "s" : ""} marked as read.`,
            "success"
          );
        }
        clearSelectedLogs(true);
      } else if (successCount > 0 && failed > 0) {
        confirmationPopupState.setPopupValues({
          content: `${successCount} message${successCount > 1 ? "s" : ""} marked as read. ${failed} failed.`,
          actionBtnText: "Ok",
          needCancelBtn: false,
          callback: () => confirmationPopupState.setShowPopup(false)
        });
        confirmationPopupState.setShowPopup(true);
        clearSelectedLogs(true);
      } else if (failed > 0) {
        confirmationPopupState.setPopupValues({
          content: `Failed to mark ${failed} message${failed > 1 ? "s" : ""} as read.`,
          actionBtnText: "Ok",
          needCancelBtn: false,
          callback: () => confirmationPopupState.setShowPopup(false)
        });
        confirmationPopupState.setShowPopup(true);
        clearSelectedLogs(true);
      }
    } else {
      toasterState.setToastMsg(
        "No messages were updated. Please try again.",
        "failure"
      );
    }
  };

  const handleMarkAsUnread = async () => {
    try {
      setMarkReadUnreadDisabled(true);
      if (!allEmailsSelectedInView) {
        const taskIds = getSelectedTaskIds();
        if (taskIds.length === 0) {
          toasterState.setToastMsg("Please select at least one email", "failure");
          return;
        }
        const totalOutboxTasks = taskIds.length;
        const response = await outboxStoreData.bulkMarkAsUnread(
          taskIds,
          getReloadOutboxCardData,
          false,
          totalOutboxTasks
        );
        handleMarkAsUnreadResponse(response, taskIds.length);
      } else {
        const totalOutboxTasks = totalElements;
        const response = await outboxStoreData.bulkMarkAsUnread(
          [],
          getReloadOutboxCardData,
          true,
          totalOutboxTasks
        );
        handleMarkAsUnreadResponse(response);
      }
    } finally {
      setMarkReadUnreadDisabled(false);
    }
  };

  const handleMarkAsUnreadResponse = (response, selectedCount = 0) => {
    if (!response) {
      const isSingle = selectedCount === 1 || selectedCount === 0;
      toasterState.setToastMsg(
        isSingle
          ? "Failed to mark message as unread."
          : "Failed to mark messages as unread. Please try again",
        "failure"
      );
      return;
    }

    const {
      success = false,
      bulkOperationResponse = {}
    } = response || {};

    if (!success) {
      const isSingle = selectedCount === 1 || selectedCount === 0;
      toasterState.setToastMsg(
        isSingle
          ? "Failed to mark message as unread."
          : "Failed to mark messages as unread. Please try again",
        "failure"
      );
      return;
    }

    const {
      activeJob = false,
      background = false,
      bulkOperationResult = {}
    } = bulkOperationResponse || {};

    if (activeJob) {
      clearSelectedLogs(true);
      SequenceUtils.showActiveJobPopupForBulkActions();
      return;
    }

    if (background) {
      confirmationPopupState.setPopupValues({
        content: "Marking messages as unread. This may take a few minutes.",
        actionBtnText: "Ok",
        needCancelBtn: false,
        callback: () => confirmationPopupState.setShowPopup(false)
      });
      confirmationPopupState.setShowPopup(true);
      clearSelectedLogs(true);
      return;
    }

    const {
      total = 0,
      success: successCount = 0,
      failed = 0
    } = bulkOperationResult || {};

    const isSingle = total === 1 || selectedCount === 1;

    if (total > 0) {
      if (successCount > 0 && failed === 0) {
        if (isSingle) {
          toasterState.setToastMsg("Message marked as unread.", "success");
        } else {
          toasterState.setToastMsg(
            `${successCount} message${successCount > 1 ? "s" : ""} marked as unread.`,
            "success"
          );
        }
        clearSelectedLogs(true);
      } else if (successCount > 0 && failed > 0) {
        confirmationPopupState.setPopupValues({
          content: `${successCount} message${successCount > 1 ? "s" : ""} marked as unread. ${failed} failed.`,
          actionBtnText: "Ok",
          needCancelBtn: false,
          callback: () => confirmationPopupState.setShowPopup(false)
        });
        confirmationPopupState.setShowPopup(true);
        clearSelectedLogs(true);
      } else if (failed > 0) {
        confirmationPopupState.setPopupValues({
          content: `Failed to mark ${failed} message${failed > 1 ? "s" : ""} as unread.`,
          actionBtnText: "Ok",
          needCancelBtn: false,
          callback: () => confirmationPopupState.setShowPopup(false)
        });
        confirmationPopupState.setShowPopup(true);
        clearSelectedLogs(true);
      }
    } else {
      toasterState.setToastMsg(
        "No messages were updated. Please try again.",
        "failure"
      );
    }
  };

  useEffect(() => {
    getSelectedCount();
  }, [inputValues]);

  return (
    <div className="headerContents">
      <div className="leftHeaderContents">
        {hideBulkActions && (
          <button
            type="button"
            className="inboxV2BackToInbox"
            onClick={() => inboxV2Store.clearSelection()}
          >
            <i className="material-icons">arrow_back</i>
            Back
          </button>
        )}
        {(showUpdateBtns || hideBulkActions) && (
          <RepliesStatusDropdown triggerCount={totalElements} />
        )}
        {name === "replied" && (
          <SentimentDropdown triggerCount={totalElements} />
        )}
        {name === "bounced" && (
          <BounceTypeDropdown triggerCount={totalElements} />
        )}
        {!dataLoading && selectedCount > 0 && (
          <ContactSelectedCount
            selectedCount={selectedCount}
            totalCount={totalElements}
            loading={dataLoading}
            allContactSelected={allEmailsSelectedInView}
            setAllContactSelected={setAllEmailsSelectedInView}
            resetCbk={() => clearSelectedLogs(true)}
            selectAllCbk={() => clearSelectedLogs(false)}
            selectedType="item"
          />
        )}
      </div>
      <div className="rightHeaderContents">
        {!hideBulkActions && (
          <SearchInputByKeyword
            id="outboxContactSearch"
            placeholder="Search name or email"
            handleSearch={handleSearchByRecipient}
            initialValue={outboxStoreData?.filterObject?.searchTerm || ""}
            debounce={400}
          />
        )}
        {showUpdateBtns && (
          <>
            <Button
              id="markAsReadBtn"
              name="markAsReadBtn"
              btnText="Mark as read"
              className="markAsReadBtn"
              type="button"
              disabled={selectedCount === 0 || dataLoading || markReadUnreadDisabled}
              click={handleMarkAsRead}
            />
            <Button
              id="markAsUnreadBtn"
              name="markAsUnreadBtn"
              btnText="Mark as unread"
              className="markAsUnreadBtn"
              type="button"
              disabled={selectedCount === 0 || dataLoading || markReadUnreadDisabled}
              click={handleMarkAsUnread}
            />
          </>
        )}
        <PageLimit {...limitObj} />
        {!hideBulkActions && !["all", "scheduled"].includes(name?.toLowerCase()) && (
          <ExportBtn
            selectedEntityCount={selectedCount || 0}
            cbk={exportCbk}
            loading={dataLoading}
            isExportEnabled={true}
            checked={selectedCount > 0}
            pageType="inbox"
            tooltipText="emails"
          />
        )}
        <FilterButton />
      </div>
    </div>
  );
});

const SingleSelectDropdown = observer(({ title, options = [], selectedValue, onSelect, loading = false, triggerCount, triggerIcon }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => !loading && setIsOpen(prev => !prev);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (id) => {
    onSelect(id);
    setIsOpen(false);
  };

  const selectedOption = options.find(opt => opt.id === selectedValue);

  return (
    <div className="singleSelectDropdown" ref={dropdownRef}>
      <div className="singleSelectDropdownControl">
        <button
          type="button"
          className={`singleSelectDropdownTrigger ${isOpen ? "dropdownOpen" : ""} ${loading ? "loading" : ""}`}
          onClick={toggleDropdown}
          disabled={loading}
        >
          <span className="singleSelectDropdownText">
            {triggerIcon && (
              <span className="singleSelectDropdownSelectedIcon">
                {triggerIcon}
              </span>
            )}
            <span className="singleSelectDropdownLabel">{loading ? "Loading..." : (selectedOption ? selectedOption.label : title)}</span>
            {!loading && (
              <span className="singleSelectDropdownCount">
                {Utils.formatNumberToString(triggerCount !== undefined ? triggerCount : (selectedOption ? selectedOption.count : 0))}
              </span>
            )}
          </span>
          <span className="singleSelectDropdownIcon">
            <Downarrow />
          </span>
        </button>
        {isOpen && (
          <div className="singleSelectDropdownMenu">
            {options.map((option) => (
              <div
                key={option.id}
                className={`singleSelectDropdownItem ${selectedValue === option.id ? "active" : ""}`}
                onClick={() => handleSelect(option.id)}
                title={option.label}
              >
                <span className="singleSelectDropdownItemLabel">
                  {option.icon && (
                    <span className="singleSelectDropdownItemIcon">
                      {option.icon}
                    </span>
                  )}
                  {option.label}
                </span>
                <span className="singleSelectDropdownItemCount">
                  ({Utils.formatNumberToString(option.count || 0)})
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

const RepliesStatusDropdown = observer(({ triggerCount }) => {
  const { repliesStats = {}, repliesStatsLoading = false, isImportant = false, repliedSubstatus = "ALL" } = outboxStoreData || {};
  const { getReloadOutboxCardData = () => { } } = useContext(OutboxContext);

  const options = [
    { label: "All Replies", id: "ALL", count: repliesStats.allCount, icon: <AllRepliesIcon /> },
    { label: "Read", id: "READ", count: repliesStats.readCount, icon: <ReadIcon /> },
    { label: "Unread", id: "UNREAD", count: repliesStats.unreadCount, icon: <UnreadIcon /> },
    { label: "Important", id: "IMPORTANT", count: repliesStats.importantCount, icon: <i className="fa fa-star-o starIcon" style={{ fontSize: '16px', color: '#535353' }}></i> }
  ];

  const handleSelect = (id) => {
    outboxStoreData.setCurrentPage(1);
    if (id === "IMPORTANT") {
      outboxStoreData.setRepliedSubstatus("NONE");
      outboxStoreData.setIsImportant(true);
    } else {
      outboxStoreData.setRepliedSubstatus(id);
      outboxStoreData.setIsImportant(false);
    }
    getReloadOutboxCardData();
  };

  const getActiveId = () => {
    if (isImportant) return "IMPORTANT";
    return repliedSubstatus;
  };

  return (
    <SingleSelectDropdown
      title="Replies"
      options={options}
      selectedValue={getActiveId()}
      onSelect={handleSelect}
      loading={repliesStatsLoading}
      triggerCount={triggerCount}
      triggerIcon={<RepliesTriggerIcon />}
    />
  );
});

const SentimentDropdown = observer(({ triggerCount }) => {
  const { repliesStats = {}, repliesStatsLoading = false } = outboxStoreData || {};
  const { getReloadOutboxCardData = () => { } } = useContext(OutboxContext);

  const options = [
    { label: "All Sentiments", id: "all", count: repliesStats.allCount, icon: <AllSentimentsIcon /> },
    { label: "Positive", id: "positive", count: repliesStats.positiveCount, icon: <PositiveIcon /> },
    { label: "Negative", id: "negative", count: repliesStats.negativeCount, icon: <NegativeIcon /> },
    { label: "Neutral", id: "neutral", count: repliesStats.neutralCount, icon: <NeutralIcon /> },
    { label: "Not Analyzed", id: "unknown", count: repliesStats.unknownCount, icon: <NotAnalyzedIcon /> }
  ];

  const handleSelect = (id) => {
    outboxStoreData.setCurrentPage(1);
    outboxStoreData.setSelectedSentiment(id);
    getReloadOutboxCardData();
  };

  return (
    <SingleSelectDropdown
      title="Sentiments"
      options={options}
      selectedValue={outboxStoreData.selectedSentiment}
      onSelect={handleSelect}
      loading={repliesStatsLoading}
      triggerCount={triggerCount}
      triggerIcon={<SentimentTriggerIcon />}
    />
  );
});

const BounceTypeDropdown = observer(({ triggerCount }) => {
  const { repliesStats = {}, repliesStatsLoading = false } = outboxStoreData || {};
  const { getReloadOutboxCardData = () => { } } = useContext(OutboxContext);

  const options = [
    { label: "All Bounces", id: "all", count: repliesStats.totalBouncedCount, icon: <AllBouncesIcon /> },
    { label: "Hard Bounce", id: "HARD_BOUNCE", count: repliesStats.hardBouncedCount, icon: <HardBounceIcon /> },
    { label: "Soft Bounce", id: "SOFT_BOUNCE", count: repliesStats.softBouncedCount, icon: <SoftBounceIcon /> }
  ];

  const handleSelect = (id) => {
    outboxStoreData.setCurrentPage(1);
    outboxStoreData.setSelectedBounceType(id);
    getReloadOutboxCardData();
  };

  return (
    <SingleSelectDropdown
      title="Bounce Types"
      options={options}
      selectedValue={outboxStoreData.selectedBounceType}
      onSelect={handleSelect}
      loading={repliesStatsLoading}
      triggerCount={triggerCount}
      triggerIcon={<BounceTriggerIcon />}
    />
  );
});


const FilterButton = () => {
  const {
    inputValues = {},
    showFilter = false,
    setShowFilter = () => { }
  } = useContext(OutboxContext);
  const { filterLoading = false, dataLoading = false } = inputValues || {};

  let isClickable = !filterLoading && !dataLoading;

  const handleClick = () => {
    isClickable && setShowFilter(!showFilter);
  };

  return (
    <div
      className={`outboxFilterIconToggle ${isClickable ? "" : "disabled"} ${outboxStoreData?.tempFilterList?.length > 0 ? "filterUsed" : ""
        }`}
      onClick={handleClick}
    >
      <FilterIcon />
      <span className="text">Filter</span>
      {(() => {
        // Count each selected item (not filter categories) — matches the
        // sequence / contacts Filter button.
        const fo = outboxStoreData?.filterObject || {};
        const checked = arr => (arr || []).filter(i => i?.checked).length;
        const total =
          checked(fo.owners) +
          checked(fo.channel) +
          checked(fo.fromAddress) +
          checked(fo.sequences) +
          checked(fo.emailType) +
          checked(fo.tags) +
          checked(fo.listIds) +
          (fo.toAddress?.length || 0) +
          (fo.dates || []).filter(d => d?.fieldName && d?.fromDate && d?.toDate)
            .length;
        return (
          <span className={`count ${total > 0 ? "" : "emptyCount"}`}>
            {total || 0}
          </span>
        );
      })()}
    </div>
  );
};

export default FilterComponent;
